import { useEffect, useRef, useState } from "react";
import MapGL, { Marker, NavigationControl, Source, Layer } from "@vis.gl/react-mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  connectVideoStreamWithDetection,
  flagForIdentifyCapture,
} from "../utils/videoUtils";


const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN as string;

declare global {
  interface Window {
    positionSocket: WebSocket | null;
    selectPersonId?: (id: string) => void;
    infoReady?: () => boolean;
  }
}

type MiniPerson = { id: string; name: string; lastLocation: string };

function useRoster() {
  const [roster, setRoster] = useState<MiniPerson[]>([]);

    useEffect(() => {
      let mounted = true;

      fetch("http://127.0.0.1:8000/api/roster")
        .then((r) => r.json())
        .then((data) => mounted && setRoster(data))
        .catch(console.error);

      return () => {
        mounted = false;
      };
    }, []);

    return roster;
 }

export default function FocusWindow() {
  const params = new URLSearchParams(window.location.search);
  const cameraId = params.get("camera") || "streetcam";
  const videoElId = "focused-video-stream";
  const videoWsRef = useRef<WebSocket | null>(null);

  const [coords, setCoords] = useState<{lat: number, lon: number}>({ lat: 40.753432, lon: -73.981046});
  const maxTrailLength = 50;
  const [trail, setTrail] = useState<[number, number][]>([]);
  const [infoData, setInfoData] = useState<string[] | null>(null);
  const [activityLog, setActivityLog] = useState<string[]>([]);
  const [typing, setTyping] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const lastActivityRef = useRef<string>("");
  const lastSeenLocRef = useRef<string>("");
  const roster = useRoster();


  useEffect(() => {
    videoWsRef.current = connectVideoStreamWithDetection(videoElId, cameraId);
    return () => videoWsRef.current?.close();
  }, [cameraId]);

  useEffect(() => {
    if (!trackingEnabled) return;

    if (!window.positionSocket || window.positionSocket.readyState > 1) {
      window.positionSocket = new WebSocket("ws://127.0.0.1:8000/ws/coordinates");

      window.positionSocket.onmessage = (e) => {
        try {
          const { lat, lon } = JSON.parse(e.data);
          console.log("Received coords:", lat, lon);
          setCoords({ lat, lon });
          setTrail((prev: [number, number][]) => {
            const updated: [number, number][] = [...prev, [lon, lat]];
            return updated.length > 50 ? updated.slice(-50) : updated;
          });
        } catch (err) {
          console.error("Tracking message parse error:", err);
        }
      };

      window.positionSocket.onerror = (err) => console.error("Tracking WS error:", err);
      window.positionSocket.onclose = () => console.log("Tracking WS closed");
    }
  }, [trackingEnabled]);

  const sendIdentifySignal = async () => {
    flagForIdentifyCapture();
    const res = await fetch("http://127.0.0.1:8000/identify_signal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ signal: "identify_clicked" }),
    });
    const { task_id } = await res.json();
    if (task_id) startResultSocket(task_id);
  };

  const startResultSocket = (taskId: string) => {
    const ws = new WebSocket(`ws://127.0.0.1:8000/ws/get_result?task_id=${taskId}`);
    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      if (data.result) {
        setInfoData(data.result);
        ws.close();
      }
    };
  };

  const sendInfoSignal = async () => {
    if (!window.infoReady?.()) {
      alert("Please wait a moment after selecting the person before clicking Info.");
      return;
    }

    const select = document.getElementById("person-select") as HTMLSelectElement;
    const trackId = select?.value;
    if (!trackId) {
      alert("Please select a person before requesting info.");
      return;
    }

    const res = await fetch("http://127.0.0.1:8000/demographics_signal", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ signal: "Person_clicked", track_id: parseInt(trackId) }),
    });
    const { task_id } = await res.json();
    if (task_id) startResultSocket(task_id);
  };

  // in your FocusWindow.tsx (or a small helper)
  const startTracking = (personId: number) => {
    // if there’s already a socket, clean it up
    if (window.positionSocket) {
      window.positionSocket.close();
    }

    const ws = new WebSocket("ws://127.0.0.1:8000/ws/coordinates");
    window.positionSocket = ws;

    ws.onopen = () => {
      // now the socket is ready, send the signal
      ws.send(JSON.stringify({
        signal: "start_tracking",
        person_id: personId,
        camera_id: cameraId,
      }));
      setTrackingEnabled(true);
    };

    ws.onmessage = (e) => {
      try {
        const msg: {
          person_id: number;
          lat: number;
          lon: number;
          activity?: string;
          seenloc?: string;
          seentime?: string;
        } = JSON.parse(e.data);
        console.log("[WS message]", msg);
        // 1) filter by the person we’re tracking
        if (msg.person_id !== personId) return;

        // 2) always update coords & trail
        setCoords({ lat: msg.lat, lon: msg.lon });
        setTrail(prev => {
          // Assert the new point is a tuple, not just number[]
          const nextPoint = [msg.lon, msg.lat] as [number, number];
          const updated = [...prev, nextPoint];
          return updated.length > maxTrailLength
            ? updated.slice(-maxTrailLength)
            : updated;
        });

        // 3) if there’s an activity field, only append on change
        const newAct = msg.activity ?? "";
        const newLoc = msg.seenloc ?? "";
        if (newAct === lastActivityRef.current || newLoc === lastSeenLocRef.current) {
          return;
        }

        lastActivityRef.current = newAct;
        lastSeenLocRef.current = newLoc;

        const ts = msg.seentime
          ? new Date(msg.seentime).toLocaleTimeString()
          : new Date().toLocaleTimeString();

        const entry = `${msg.seentime} Person ${personId}: ${msg.activity} at ${msg.seenloc}`;
          setActivityLog((prev) => [...prev, entry]);

      } catch (err) {
        console.error("WS parse error:", err);
      }
    };

    ws.onerror = (err) => console.error("tracking ws error", err);
    ws.onclose = () => console.log("tracking ws closed");
  };


  const sendTrackSignal = () => {
    const sel = document.getElementById("person-select") as HTMLSelectElement;
    const id = parseInt(sel.value || "", 10);
    if (!id) {
      return alert("Please select a person before tracking.");
    }
    startTracking(id);
  };


  const getBearing = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const toRad = (deg: number) => deg * Math.PI / 180;
    const toDeg = (rad: number) => rad * 180 / Math.PI;
    const dLon = toRad(lon2 - lon1);
    const y = Math.sin(dLon) * Math.cos(toRad(lat2));
    const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
              Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
  };

  const bearing = trail.length >= 2
    ? getBearing(trail[trail.length - 2][1], trail[trail.length - 2][0], coords.lat, coords.lon)
    : 0;

  return (
      <div className="flex w-full h-screen bg-slate-900 text-gray-100 p-4">
        <div className="flex flex-col w-2/3 bg-slate-800 rounded-2xl shadow-lg p-4">
          <img id={videoElId} alt="Focused Stream" className="w-full h-[460px] object-cover rounded-lg border border-slate-700 mb-4"/>

          <div className="flex gap-2 mb-4 justify-center">
            <button onClick={sendIdentifySignal} className="bg-blue-600 text-white px-4 py-2 rounded-xl">Identify
            </button>
            <button onClick={sendTrackSignal} className="bg-green-600 text-white px-4 py-2 rounded-xl">Track</button>
            <button onClick={sendInfoSignal} className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-xl transition">Info</button>
            <select id="person-select" onChange={(e) => window.selectPersonId?.(e.target.value)}
                    className="ml-4 bg-slate-700 text-gray-200 border border-slate-600 px-3 py-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Show all</option>
            </select>
            <button onClick={() => alert("LLM summary request will go here.")}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl">Generate Summary
            </button>
          </div>
          <div
              className="overflow-y-auto h-[365px] space-y-1 p-4 bg-black text-green-400 font-mono text-sm rounded-xl border border-slate-700">
            {activityLog.length > 0 ? (
                activityLog.map((line, idx) => (
                    <p key={idx} className="whitespace-pre-wrap">{line}</p>
                ))
            ) : (
                <p className="text-gray-500">Waiting for activity data...</p>
            )}
          </div>
        </div>

        <div className="w-1/3 flex flex-col pl-4">
          <div className="bg-slate-800 rounded-xl p-4 mb-4 shadow-lg h-[350px] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Roster</h2>

            {roster.length ? (
                <ul className="space-y-2 text-sm">
                  {roster.map((p) => (
                      <li
                          key={p.id}
                          className="flex justify-between bg-slate-700/60
                             px-3 py-2 rounded-lg hover:bg-slate-700/80"
                      >
                        <span className="font-medium">{p.name}</span>
                        <span className="text-gray-300">{p.lastLocation}</span>
                      </li>
                  ))}
                </ul>
            ) : (
                <p className="text-gray-400">Loading roster…</p>
            )}
          </div>

          <div className="rounded-xl overflow-hidden shadow-lg h-[550px] w-full border border-slate-700">
            <MapGL
                mapboxAccessToken={MAPBOX_TOKEN}
                initialViewState={{longitude: -73.981046, latitude: 40.753432, zoom: 17}}
                style={{width: "100%", height: "100%"}}
                mapStyle="mapbox://styles/mapbox/streets-v11"
            >
              <NavigationControl position="top-right"/>

              {/* Only show breadcrumb trail when tracking is on */}
              {trackingEnabled && trail.length > 1 && (
                  <Source
                      id="trail"
                      type="geojson"
                      data={{
                        type: "Feature",
                        geometry: {type: "LineString", coordinates: trail}
                      } as any}
                  >
                    <Layer
                        id="trail-line"
                        type="line"
                        paint={{"line-color": "#f00", "line-width": 3}}
                    />
                  </Source>
              )}

              {/* Only show direction arrow when tracking */}
              {trackingEnabled && (
                  <Marker
                      longitude={coords.lon}
                      latitude={coords.lat}
                      anchor="center"
                  >
                    <div
                        className="w-4 h-4 rounded-full bg-red-500 border-2 border-white"
                        title={`Person @ ${coords.lat.toFixed(5)}, ${coords.lon.toFixed(5)}`}
                    />
                  </Marker>
              )}
            </MapGL>
          </div>
        </div>
      </div>
  );
}
